<template>
  <div class="dashboard-page">
    <Head title="Dashboard" />

    <!-- Header -->
    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">
          Overview
        </p>
        <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">
          Dashboard
        </h1>
        <p class="mt-1.5 text-sm text-gray-500">
          Submission activity across web and mobile for the selected period.
        </p>
      </div>

      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div class="inline-flex items-center rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
          <button
            v-for="option in rangeOptions"
            :key="option.value"
            type="button"
            class="rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
            :class="
              currentRange === option.value
                ? 'bg-brand-600 text-white shadow-sm'
                : 'text-gray-500 hover:bg-gray-50 hover:text-brand-700'
            "
            @click="changeRange(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <div class="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
          <input
            v-model="dateFrom"
            type="date"
            class="rounded-md border-0 bg-transparent px-1 py-0.5 text-xs font-medium text-gray-700 focus:outline-none focus:ring-0"
          />
          <span class="text-xs text-gray-400">→</span>
          <input
            v-model="dateTo"
            type="date"
            class="rounded-md border-0 bg-transparent px-1 py-0.5 text-xs font-medium text-gray-700 focus:outline-none focus:ring-0"
          />
          <button
            type="button"
            class="rounded-lg bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700"
            @click="applyCustomDates"
          >
            Apply
          </button>
        </div>
      </div>
    </div>

    <!-- Metric cards -->
    <div class="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <div class="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div class="absolute inset-y-0 left-0 w-1 bg-brand-600" />
        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Total submissions
        </p>
        <p class="mt-3 text-3xl font-bold tracking-tight text-brand-900">
          {{ formatNumber(metrics.total) }}
        </p>
        <p class="mt-2 text-xs text-gray-500">
          {{ rangeDescription }}
        </p>
      </div>

      <div class="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div class="absolute inset-y-0 left-0 w-1 bg-amber-500" />
        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Web submissions
        </p>
        <p class="mt-3 text-3xl font-bold tracking-tight text-brand-900">
          {{ formatNumber(metrics.total_web) }}
        </p>
        <p class="mt-2 text-xs text-gray-500">
          {{ webShare }}% of total
        </p>
      </div>

      <div class="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div class="absolute inset-y-0 left-0 w-1 bg-teal-500" />
        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
          App submissions
        </p>
        <p class="mt-3 text-3xl font-bold tracking-tight text-brand-900">
          {{ formatNumber(metrics.total_mobile) }}
        </p>
        <p class="mt-2 text-xs text-gray-500">
          {{ appShare }}% of total
        </p>
      </div>

      <div class="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-brand-600 to-brand-800 p-5 text-white shadow-sm">
        <p class="text-xs font-semibold uppercase tracking-wide text-white/70">
          Period range
        </p>
        <p class="mt-3 text-lg font-bold leading-snug">
          {{ periodHeadline }}
        </p>
        <p class="mt-2 text-xs text-white/70">
          {{ periodDates }}
        </p>
      </div>
    </div>

    <!-- Charts + side panel -->
    <div class="grid grid-cols-1 gap-5 xl:grid-cols-12">
      <!-- Trend chart -->
      <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-8">
        <div class="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-base font-bold text-brand-900">Submissions over time</h2>
            <p class="text-xs text-gray-500">
              Daily totals for web and app
              <span v-if="currentRange === 'all'">(monthly for all-time)</span>
            </p>
          </div>
          <div class="flex items-center gap-4 text-xs font-medium text-gray-500">
            <span class="inline-flex items-center gap-1.5">
              <span class="h-2.5 w-2.5 rounded-full bg-amber-500" /> Web
            </span>
            <span class="inline-flex items-center gap-1.5">
              <span class="h-2.5 w-2.5 rounded-full bg-teal-500" /> App
            </span>
          </div>
        </div>

        <div v-if="chartPoints.length" class="relative h-80">
          <canvas ref="trendChart" />
        </div>
        <div
          v-else
          class="flex h-80 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500"
        >
          No submission data for this period.
        </div>
      </div>

      <!-- Right column -->
      <div class="flex flex-col gap-5 xl:col-span-4">
        <!-- Mix chart -->
        <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 class="text-base font-bold text-brand-900">Channel mix</h2>
          <p class="mt-0.5 text-xs text-gray-500">Web vs app share in this period</p>

          <div v-if="metrics.total > 0" class="relative mx-auto mt-4 h-52 w-full max-w-[220px]">
            <canvas ref="mixChart" />
          </div>
          <div
            v-else
            class="mt-4 flex h-52 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500"
          >
            No data
          </div>

          <div class="mt-3 grid grid-cols-2 gap-3">
            <div class="rounded-xl bg-amber-50 px-3 py-2.5">
              <p class="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Web</p>
              <p class="mt-1 text-lg font-bold text-brand-900">{{ webShare }}%</p>
            </div>
            <div class="rounded-xl bg-teal-50 px-3 py-2.5">
              <p class="text-[11px] font-semibold uppercase tracking-wide text-teal-700">App</p>
              <p class="mt-1 text-lg font-bold text-brand-900">{{ appShare }}%</p>
            </div>
          </div>
        </div>

        <!-- Quick links -->
        <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 class="text-base font-bold text-brand-900">Quick links</h2>
          <p class="mt-0.5 text-xs text-gray-500">Jump to common workflows</p>

          <div class="mt-4 space-y-2">
            <Link
              v-for="item in quickLinks"
              :key="item.href"
              :href="item.href"
              class="group flex items-center justify-between rounded-xl border border-gray-200 px-3.5 py-3 transition-colors hover:border-brand-300 hover:bg-brand-50"
            >
              <div>
                <p class="text-sm font-semibold text-brand-900 group-hover:text-brand-700">
                  {{ item.title }}
                </p>
                <p class="text-xs text-gray-500">{{ item.subtitle }}</p>
              </div>
              <span class="text-xs font-semibold text-brand-500 group-hover:text-brand-700">
                Open →
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import moment from 'moment'
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
)

export default {
  components: {
    Head,
    Link,
  },
  layout: Layout,
  props: {
    metrics: {
      type: Object,
      required: true,
    },
    dailySubmissions: {
      type: Array,
      default: () => [],
    },
    currentRange: {
      type: String,
      default: 'week',
    },
  },
  data() {
    return {
      dateFrom: this.metrics?.from || '',
      dateTo: this.metrics?.to || '',
      trendChartInstance: null,
      mixChartInstance: null,
      rangeOptions: [
        { value: 'day', label: '1D' },
        { value: 'week', label: '1W' },
        { value: 'month', label: '1M' },
        { value: 'all', label: 'All' },
      ],
      quickLinks: [
        { href: '/submissionweb', title: 'Landing Web', subtitle: 'Web submission list' },
        { href: '/submissionmobile', title: 'Android / iOS', subtitle: 'App submission list' },
        { href: '/custmanagement', title: 'Tracking Management', subtitle: 'User tracking data' },
        { href: '/campaign', title: 'Campaign', subtitle: 'Campaign configuration' },
      ],
    }
  },
  computed: {
    webShare() {
      if (!this.metrics?.total) return 0
      return Math.round((this.metrics.total_web / this.metrics.total) * 100)
    },
    appShare() {
      if (!this.metrics?.total) return 0
      return Math.round((this.metrics.total_mobile / this.metrics.total) * 100)
    },
    periodHeadline() {
      if (this.currentRange === 'day') return 'Last 1 day'
      if (this.currentRange === 'month') return 'Last 30 days'
      if (this.currentRange === 'all') return 'All time'
      if (this.currentRange === 'custom') return 'Custom range'
      return 'Last 7 days'
    },
    periodDates() {
      const from = this.metrics?.from
      const to = this.metrics?.to
      if (!from || !to) return '—'
      return `${moment(from).format('DD MMM YY')} – ${moment(to).format('DD MMM YY')}`
    },
    rangeDescription() {
      return `${this.periodHeadline} · ${this.periodDates}`
    },
    chartPoints() {
      if (!this.dailySubmissions.length) {
        return []
      }

      if (this.currentRange === 'all') {
        return this.dailySubmissions.map((d) => ({
          label: moment(d.date).format('MMM YY'),
          web: d.web || 0,
          mobile: d.mobile || 0,
        }))
      }

      const dataByDate = this.dailySubmissions.reduce((acc, d) => {
        const key = moment(d.date).format('YYYY-MM-DD')
        acc[key] = {
          web: d.web || 0,
          mobile: d.mobile || 0,
        }
        return acc
      }, {})

      const from = this.metrics?.from ? moment(this.metrics.from) : null
      const to = this.metrics?.to ? moment(this.metrics.to) : null

      if (!from || !to || !from.isValid() || !to.isValid()) {
        return this.dailySubmissions.map((d) => ({
          label: moment(d.date).format('DD MMM'),
          web: d.web || 0,
          mobile: d.mobile || 0,
        }))
      }

      const filled = []
      const cursor = from.clone()
      while (cursor.isSameOrBefore(to, 'day')) {
        const key = cursor.format('YYYY-MM-DD')
        const item = dataByDate[key] || { web: 0, mobile: 0 }
        filled.push({
          label: cursor.format('DD MMM'),
          web: item.web,
          mobile: item.mobile,
        })
        cursor.add(1, 'day')
      }

      return filled
    },
  },
  watch: {
    chartPoints: {
      deep: true,
      handler() {
        this.$nextTick(() => this.renderCharts())
      },
    },
    metrics: {
      deep: true,
      handler() {
        this.dateFrom = this.metrics?.from || ''
        this.dateTo = this.metrics?.to || ''
        this.$nextTick(() => this.renderCharts())
      },
    },
  },
  mounted() {
    this.renderCharts()
  },
  beforeUnmount() {
    this.destroyCharts()
  },
  // Vue 2/3 Options API alias used by Vue 3
  beforeDestroy() {
    this.destroyCharts()
  },
  methods: {
    changeRange(range) {
      if (range === this.currentRange) return
      this.$inertia.get('/', { range }, { preserveState: false, preserveScroll: true })
    },
    applyCustomDates() {
      if (!this.dateFrom && !this.dateTo) return
      const params = {}
      if (this.dateFrom) params.from = this.dateFrom
      if (this.dateTo) params.to = this.dateTo
      this.$inertia.get('/', params, { preserveState: false, preserveScroll: true })
    },
    formatNumber(value) {
      if (value == null) return '0'
      return value.toLocaleString('id-ID')
    },
    destroyCharts() {
      if (this.trendChartInstance) {
        this.trendChartInstance.destroy()
        this.trendChartInstance = null
      }
      if (this.mixChartInstance) {
        this.mixChartInstance.destroy()
        this.mixChartInstance = null
      }
    },
    renderCharts() {
      this.renderTrendChart()
      this.renderMixChart()
    },
    renderTrendChart() {
      if (!this.$refs.trendChart || !this.chartPoints.length) {
        if (this.trendChartInstance) {
          this.trendChartInstance.destroy()
          this.trendChartInstance = null
        }
        return
      }

      if (this.trendChartInstance) {
        this.trendChartInstance.destroy()
      }

      const labels = this.chartPoints.map((p) => p.label)
      const webData = this.chartPoints.map((p) => p.web)
      const appData = this.chartPoints.map((p) => p.mobile)

      this.trendChartInstance = new Chart(this.$refs.trendChart, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Web',
              data: webData,
              backgroundColor: 'rgba(245, 158, 11, 0.85)',
              hoverBackgroundColor: 'rgba(217, 119, 6, 0.95)',
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: 28,
            },
            {
              label: 'App',
              data: appData,
              backgroundColor: 'rgba(20, 184, 166, 0.85)',
              hoverBackgroundColor: 'rgba(13, 148, 136, 0.95)',
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: 28,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#2f282a',
              titleFont: { family: "'Plus Jakarta Sans', sans-serif", size: 12, weight: '600' },
              bodyFont: { family: "'Plus Jakarta Sans', sans-serif", size: 12 },
              padding: 10,
              cornerRadius: 8,
              callbacks: {
                label(context) {
                  const value = context.parsed.y || 0
                  return ` ${context.dataset.label}: ${value.toLocaleString('id-ID')}`
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: { family: "'Plus Jakarta Sans', sans-serif", size: 11 },
                color: '#736266',
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: 10,
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(85, 71, 74, 0.08)',
                drawBorder: false,
              },
              ticks: {
                font: { family: "'Plus Jakarta Sans', sans-serif", size: 11 },
                color: '#736266',
                precision: 0,
                callback(value) {
                  return Number(value).toLocaleString('id-ID')
                },
              },
            },
          },
        },
      })
    },
    renderMixChart() {
      if (!this.$refs.mixChart || !this.metrics?.total) {
        if (this.mixChartInstance) {
          this.mixChartInstance.destroy()
          this.mixChartInstance = null
        }
        return
      }

      if (this.mixChartInstance) {
        this.mixChartInstance.destroy()
      }

      this.mixChartInstance = new Chart(this.$refs.mixChart, {
        type: 'doughnut',
        data: {
          labels: ['Web', 'App'],
          datasets: [
            {
              data: [this.metrics.total_web || 0, this.metrics.total_mobile || 0],
              backgroundColor: ['#f59e0b', '#14b8a6'],
              hoverBackgroundColor: ['#d97706', '#0d9488'],
              borderWidth: 0,
              cutout: '68%',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#2f282a',
              titleFont: { family: "'Plus Jakarta Sans', sans-serif", size: 12, weight: '600' },
              bodyFont: { family: "'Plus Jakarta Sans', sans-serif", size: 12 },
              padding: 10,
              cornerRadius: 8,
              callbacks: {
                label(context) {
                  const value = context.parsed || 0
                  return ` ${context.label}: ${value.toLocaleString('id-ID')}`
                },
              },
            },
          },
        },
      })
    },
  },
}
</script>
